import moment from 'moment';
import React, { FC } from 'react';
import { Control, FieldError, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ClockIcon } from 'static';
import { getSettings } from 'store/selectors/auth';

import { StyledErrorMessage, StyledLabel } from '../styled';
import {
  StyledPickerErrorWrapper,
  StyledPickerWrapper,
  StyledTimeIcon,
  StyledTimePicker,
} from './styled';

interface TimePickerProps {
  name: string;
  control: Control;
  label?: string;
  width?: string;
  error?: FieldError;
  setValue?: any;
  formWatcher?: any;
  watchFieldName?: string;
}

const TimePicker: FC<TimePickerProps> = ({
  name,
  control,
  label,
  width,
  error,
  setValue,
  formWatcher,
  watchFieldName,
}) => {
  const { t } = useTranslation();
  const timeFormat = useSelector(getSettings)?.timeFormat;

  const { field } = useController({
    name,
    control,
  });

  const onChange = (value: Date) => {
    if (!value) {
      return;
    }

    // Get the current selected time
    const selectedTime = moment(value);

    // If this is an end time field and we have a start time to compare against
    if (name.includes('to') && watchFieldName && formWatcher) {
      const startTime = moment(formWatcher[0]); // Get start time from watcher

      // If end time is before start time, adjust the date to next day
      if (selectedTime.isBefore(startTime)) {
        const adjustedTime = selectedTime.add(1, 'day');
        field.onChange(adjustedTime.toDate());
        return;
      }
    }

    // For start time or normal cases
    field.onChange(value);

    // If this is start time and we have an end time, check if we need to adjust end time
    if (name.includes('from') && watchFieldName && setValue && formWatcher) {
      const endTime = moment(formWatcher[1]); // Get end time from watcher
      const newStartTime = moment(value);

      // If end time is now before the new start time, adjust it to be 15 minutes after start
      if (endTime.isBefore(newStartTime)) {
        const adjustedEndTime = moment(value).add(15, 'minutes');
        setValue(watchFieldName, adjustedEndTime.toDate());
      }
    }
  };

  return (
    <StyledPickerErrorWrapper>
      <StyledPickerWrapper width={width}>
        <StyledLabel htmlFor={name}>{label}</StyledLabel>
        <StyledTimePicker
          id={name}
          selected={field.value}
          dateFormat={timeFormat === 12 ? 'h:mm aa' : 'HH:mm'}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={15}
          timeFormat={timeFormat === 12 ? 'h:mm aa' : 'HH:mm'}
          error={error}
          onChange={onChange}
        />
        <StyledTimeIcon src={ClockIcon} alt="" />
      </StyledPickerWrapper>
      {error && (
        <StyledErrorMessage>
          {error.message || t(`errors.${error.type}`)}
        </StyledErrorMessage>
      )}
    </StyledPickerErrorWrapper>
  );
};

export default TimePicker;
